import React from "react";
import { Box, Button, Alert, Stack, Typography } from "@mui/material";
import WarningAmber from "@mui/icons-material/WarningAmber";
import { useTranslation } from "react-i18next";
import { Coverage } from "../Calibration.types";
import type { SubmitNotice } from "../useCalibration";

interface CalibrationActionAreaProps {
  canCalibrate: boolean;
  loading: boolean;
  coverage: Coverage;
  averageAppleWeight: number | null;
  onRunCalibration: () => void;
  edgeSampleCount: number;
  sampleCount: number;
  notice?: SubmitNotice | null;
}

export function CalibrationActionArea({
  canCalibrate,
  loading,
  coverage,
  averageAppleWeight,
  onRunCalibration,
  edgeSampleCount,
  sampleCount,
  notice = null,
}: CalibrationActionAreaProps) {
  const { t } = useTranslation();

  const showRequirements = !canCalibrate;
  const showEdgeWarning = edgeSampleCount > 0;

  return (
    <Box
      sx={{
        p: 3,
        bgcolor: "var(--color-surface)",
        borderTop: 1,
        borderColor: "divider",
      }}
    >
      <Button
        loading={loading}
        variant="contained"
        fullWidth
        size="large"
        disabled={!canCalibrate}
        sx={{ py: 1.5, fontSize: "1rem" }}
        onClick={onRunCalibration}
      >
        {canCalibrate
          ? t("text.calibration.run")
          : t("text.calibration.missing-requirements")}
      </Button>

      {notice?.severity === "error" && (
        <Box sx={{ pt: 1 }}>
          <Alert variant="filled" severity="error">
            {notice.text}
          </Alert>
        </Box>
      )}

      {notice?.severity === "success" && (
        <Alert severity="success" sx={{ mt: 2 }}>
          {notice.text}
        </Alert>
      )}

      {showRequirements && (
        <Alert severity="warning" icon={<WarningAmber />} sx={{ mt: 2 }}>
          <Stack spacing={0.25}>
            {Object.keys(coverage).map((key) => {
              const item = coverage[key];
              const label = `text.calibrate.required_counts.yield-category-${item.bucketIndex}`;
              if (item.count < item.minCount) {
                return (
                  <Typography variant="body2" key={key}>
                    {`• ${t(label, { count: item.minCount - item.count })}`}
                  </Typography>
                );
              }
              return null;
            })}

            {!averageAppleWeight && (
              <Typography variant="body2">
                {`• ${t("text.calibrate.average_weight")} ${t("text.error.is_required")}`}
              </Typography>
            )}
          </Stack>
        </Alert>
      )}

      {showEdgeWarning && (
        <Alert severity="info" sx={{ mt: 2 }}>
          <Typography variant="body2">
            {t("text.calibrate.edge_samples_warning", {
              edgeCount: edgeSampleCount,
              total: sampleCount,
            })}
          </Typography>
        </Alert>
      )}
    </Box>
  );
}
